import * as Yup from 'yup';
import {
  FormBlock,
  FormMultipleDatePickerField,
  FormSelectField,
  FormTextField,
  Typography,
} from 'omakase';
import { Grid } from 'ui';
import { LocalTime } from 'js-joda';
import {
  calendarOpenDateRangeSelector,
  calendarTicketTypesSelector,
} from 'selectors/calendarSelectors';
import { constrainTimeOptions } from 'components/business/waitlist-dialog/WaitlistDialogPreferences';
import { dialogFormBlock } from 'components/business/waitlist-dialog/waitlistPreferencesFormCssDefs';
import { displayedTimesSelector } from 'selectors/displayedTimesSelectors';
import { getBusinessConfigSelector } from 'selectors/appSelector';
import { getTimeFormatter } from '@tock/i18n';
import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

export type WaitlistPreferencesFields = {
  desiredPartySize: string;
  desiredExperience: string;
  desiredDates: FormMultipleDatePickerField;
  desiredStartTime: string;
  desiredEndTime: string;
};

/**
 * todo - figure out typings for array of local dates in a Yup schema.
 */
export const waitlistPreferencesSchema: Yup.SchemaOf<WaitlistPreferencesFields> = Yup.object({
  desiredPartySize: Yup.string().required().label('Party size'),
  desiredExperience: Yup.string().required().label('Experience'),
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  desiredDates: Yup.array<any, any>().required().min(1, 'Please select at least one date'),
  desiredStartTime: Yup.string().required().label('Start time'),
  desiredEndTime: Yup.string().required().label('End time'),
});

export const ANY_EXPERIENCE_OPTION = 'ANY';

/**
 * Party size options can be either a combined list of all sizes from every offering OR
 * the sizes offered for the desired experience.
 */
const useGetPartySizeOptions = (desiredExperienceId: string) => {
  const allExperiences = useSelector(calendarTicketTypesSelector);
  const desiredExperience = allExperiences.find(({ id }) => id === Number(desiredExperienceId));

  // Fall back to the union of every offering's sizes unless the desired
  // experience has its own sizes. Note: an empty array is truthy, so guard on
  // length rather than existence, otherwise an experience with no sizes returns
  // [] and never falls back.
  if (desiredExperience?.purchaseSize?.length) {
    return [...desiredExperience.purchaseSize].sort((a, b) => a - b);
  }

  return [...new Set(allExperiences.flatMap((e) => e.purchaseSize ?? []))].sort((a, b) => a - b);
};

/**
 * Form fields to collect information about a waitlist request
 */
export const WaitlistPreferencesForm = () => {
  const { values, setFieldValue } = useFormikContext<WaitlistPreferencesFields>();

  const allTimeOptions = useSelector(displayedTimesSelector);
  const business = useSelector(getBusinessConfigSelector);
  const ticketTypes = useSelector(calendarTicketTypesSelector);
  const openDateRange = useSelector(calendarOpenDateRangeSelector);

  const sizeOptions = useGetPartySizeOptions(values.desiredExperience);

  useEffect(() => {
    // Only reset the party size when there is a valid size to select. Without the
    // length check, an empty sizeOptions makes this condition always true and sets
    // the field to "undefined" on every render, causing an infinite update loop.
    if (sizeOptions.length > 0 && !sizeOptions.includes(Number(values.desiredPartySize))) {
      setFieldValue('desiredPartySize', `${sizeOptions[0]}`);
    }
  }, [sizeOptions, values.desiredPartySize, setFieldValue]);

  const timeOptions = constrainTimeOptions(
    values.desiredStartTime,
    values.desiredEndTime,
    allTimeOptions
  );

  const timeFormatter = getTimeFormatter(business?.locale || 'en-US');

  return (
    <FormBlock showDivider={false} css={dialogFormBlock}>
      <Typography variant="body02">
        Tell us what you're interested in and we'll let you know if something opens up.
      </Typography>
      <Typography variant="heading04">{business?.name}</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormSelectField label="Party size" name="desiredPartySize" id="desiredPartySize">
            {sizeOptions.map((size) => (
              <option key={size} value={`${size}`}>
                {size}
              </option>
            ))}
          </FormSelectField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormSelectField label="Experience" name="desiredExperience" id="desiredExperience">
            <option value={ANY_EXPERIENCE_OPTION}>Any experience</option>
            {ticketTypes.map((tt) => (
              <option key={tt.id} value={`${tt.id}`}>
                {tt.name}
              </option>
            ))}
          </FormSelectField>
        </Grid>
      </Grid>

      <FormMultipleDatePickerField
        label="Date"
        name="desiredDates"
        id="desiredDates"
        placeholder="Choose date(s)"
        DatePickerProps={
          openDateRange
            ? {
                earliestSelectableDate: openDateRange.earliestDate,
                latestSelectableDate: openDateRange.latestDate,
              }
            : {}
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormSelectField label="Start time" name="desiredStartTime">
            {timeOptions.startTimes.map((time) => (
              <option key={time} value={time}>
                {timeFormatter.format(LocalTime.parse(time))}
              </option>
            ))}
          </FormSelectField>
        </Grid>
        <Grid item xs={6}>
          <FormSelectField label="End time" name="desiredEndTime">
            {timeOptions.endTimes.map((time) => (
              <option key={time} value={time}>
                {timeFormatter.format(LocalTime.parse(time))}
              </option>
            ))}
          </FormSelectField>
        </Grid>
      </Grid>

      <FormTextField
        label="Notes"
        multiline
        placeholder="Please specify anything else we should know"
        name="guestNote"
        rows={5}
      />
    </FormBlock>
  );
};
